/**
 * Editors for article.cards (admin). Up/down reorder; uploads to Storage
 * articles/{articleId}/cards/{cardId}.jpg (max 1200px JPEG; transparent → white bg).
 */

import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Div,
  FeedbackFormInput,
  FeedbackFormTextarea,
  P,
} from "../../../styles/styles";
import { LANGS } from "../../../utils/articleHelpers";
import { pickLang } from "../../../utils/i18nDisplay";
import {
  deleteArticleCardImage,
  uploadArticleImage,
} from "../../../utils/uploadArticleImage";

const emptyI18n = () => ({
  en: "",
  ru: "",
  sr: "",
});

export function createEmptyArticleCard() {
  return {
    id: uuidv4(),
    title: emptyI18n(),
    bodyHtml: emptyI18n(),
    imageUrl: "",
  };
}

function cardTitlePreview(card) {
  const t = pickLang(card?.title || {}, "en");
  if (t && String(t).trim()) return String(t).trim();
  if (card?.imageUrl) return "(image only)";
  return "(empty)";
}

export default function AdminArticleCardsSection({
  articleFirestoreId,
  cards,
  onChangeCards,
}) {
  const isDesktop = useMediaQuery({ minWidth: "800px" });
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());
  const upsertCards = (next) => onChangeCards(next);

  const toggleCollapsed = (cardId) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const addCard = () => upsertCards([...cards, createEmptyArticleCard()]);

  const move = (idx, dir) => {
    const copy = [...cards];
    const j = idx + dir;
    if (j < 0 || j >= copy.length) return;
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    upsertCards(copy);
  };

  const dupCard = (idx) => {
    const copy = [...cards];
    const base = { ...copy[idx], id: uuidv4() };
    copy.splice(idx + 1, 0, base);
    upsertCards(copy);
  };

  const removeCard = async (idx) => {
    const card = cards[idx];
    if (articleFirestoreId && card?.id && card?.imageUrl) {
      try {
        await deleteArticleCardImage(articleFirestoreId, card.id);
      } catch (err) {
        window.alert(err?.message || "Failed to delete card image");
        return;
      }
    }
    if (card?.id) {
      setCollapsedIds((prev) => {
        const next = new Set(prev);
        next.delete(card.id);
        return next;
      });
    }
    upsertCards(cards.filter((_, i) => i !== idx));
  };

  const setCardLang = (idx, patch) => {
    const copy = [...cards];
    copy[idx] = { ...copy[idx], ...patch };
    upsertCards(copy);
  };

  const patchCardNested = (idx, nestedKey, lang, val) => {
    const card = cards[idx];
    const nextNested = {
      ...(card[nestedKey] || {}),
      [lang]: val,
    };
    setCardLang(idx, { [nestedKey]: nextNested });
  };

  const onPickFile = async (idx, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !articleFirestoreId || !cards[idx]?.id) return;
    setUploadingIdx(idx);
    setUploadError("");
    try {
      const url = await uploadArticleImage({
        articleId: articleFirestoreId,
        kind: "card",
        cardId: cards[idx].id,
        file,
      });
      setCardLang(idx, { imageUrl: url });
    } catch (err) {
      setUploadError(err?.message || "Upload failed");
    } finally {
      setUploadingIdx(null);
    }
  };

  const onClearImage = async (idx) => {
    const card = cards[idx];
    if (articleFirestoreId && card?.id && card?.imageUrl) {
      try {
        await deleteArticleCardImage(articleFirestoreId, card.id);
      } catch (err) {
        window.alert(err?.message || "Failed to delete image");
        return;
      }
    }
    setCardLang(idx, { imageUrl: "" });
  };

  return (
    <Div width="100%" gap="12px" margin="10px 0 0 0" alignStart>
      <P fontSize="18px" fontWeight="700" margin="0" textAlign="left">
        Content cards (slides)
      </P>

      {!articleFirestoreId && (
        <P fontSize="13px" style={{ color: "#c62828" }} textAlign="left" margin="0">
          After you create the article once, uploads use Firebase Storage paths. You can paste image URLs before that.
        </P>
      )}
      {uploadError ? (
        <P
          fontSize="13px"
          style={{ color: "#c62828" }}
          textAlign="left"
          margin="0"
          role="alert"
        >
          {uploadError}
        </P>
      ) : null}

      {cards.map((card, idx) => {
        const cardKey = card.id || `idx-${idx}`;
        const isCollapsed = collapsedIds.has(card.id);

        return (
          <Div key={cardKey} width="100%" gap="8px" alignStart>
            <Div
              width="100%"
              padding="12px"
              borderRadius="8px"
              style={{ border: "1px solid #e0e0e0", background: "#fafafa" }}
              gap={isCollapsed ? "0" : "30px"}
            >
              <Div row gap="8px" justifySpace width="100%" align="center">
                <Div row gap="8px" align="center" flex="1" justifyStart>
                  <P fontWeight="600" margin="0" textAlign="left">
                    Card #{idx + 1}
                  </P>
                  {isCollapsed ? (
                    <P
                      margin="0"
                      fontSize="13px"
                      textAlign="left"
                      style={{ color: "#666" }}
                    >
                      — {cardTitlePreview(card)}
                    </P>
                  ) : null}
                </Div>
                <Div row justifyEnd gap="10px" wrap="wrap">
                  <Button
                    type="button"
                    width="auto"
                    outlineButton
                    fontSize="13px"
                    padding="5px 14px"
                    height="auto"
                    onClick={() => toggleCollapsed(card.id)}
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? "Expand card" : "Collapse card"}
                  >
                    {isCollapsed ? "Expand" : "Collapse"}
                  </Button>
                  <Button
                    type="button"
                    width="auto"
                    outlineButton
                    fontSize="13px"
                    padding="5px 20px"
                    height="auto"
                    onClick={() => move(idx, -1)}
                    disabled={idx <= 0}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    width="auto"
                    fontSize="13px"
                    outlineButton
                    padding="5px 20px"
                    height="auto"
                    onClick={() => move(idx, 1)}
                    disabled={idx >= cards.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    width="auto"
                    outlineButton
                    fontSize="13px"
                    padding="5px 20px"
                    height="auto"
                    onClick={() => dupCard(idx)}
                  >
                    Duplicate
                  </Button>
                  <Button
                    type="button"
                    outlineButton
                    width="auto"
                    fontSize="11px"
                    padding="5px 20px"
                    height="auto"
                    onClick={() => removeCard(idx)}
                  >
                    Remove
                  </Button>
                </Div>
              </Div>

              {!isCollapsed ? (
                <>
                  <Div gap="10px">
                    <P margin="0" fontSize="13px" textAlign="left" style={{ color: "#444" }}>
                      Image (optional — upload replaces URL)
                    </P>
                    {card.imageUrl ? (
                      <img
                        alt=""
                        src={card.imageUrl}
                        style={{
                          maxWidth: 180,
                          borderRadius: 8,
                          border: "1px solid #eee",
                        }}
                      />
                    ) : null}
                    <Div gap="20px" alignStart>
                      <label
                        style={{
                          cursor:
                            articleFirestoreId && uploadingIdx !== idx
                              ? "pointer"
                              : "not-allowed",
                        }}
                      >
                        <P textAlign="left" fontWeight="600">
                          {uploadingIdx === idx
                            ? "Uploading…"
                            : card.imageUrl
                              ? "Replace image"
                              : "Upload file"}
                        </P>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          disabled={!articleFirestoreId || uploadingIdx === idx}
                          onChange={(e) => onPickFile(idx, e)}
                          aria-label={`Upload slide ${idx + 1} image`}
                          style={{ marginTop: 10 }}
                        />
                      </label>
                      <Div width="50%">
                        <FeedbackFormInput
                          value={card.imageUrl || ""}
                          onChange={(e) =>
                            setCardLang(idx, { imageUrl: e.target.value })
                          }
                          placeholder="https://firebasestorage.googleapis.com/..."
                          aria-label={`Slide ${idx + 1} image URL`}
                          style={{
                            padding: "3px 5px",
                            border: "1px gray solid",
                            width: "100%",
                          }}
                        />
                      </Div>
                      <Button
                        type="button"
                        width="auto"
                        outlineButton
                        fontSize="13px"
                        padding="5px 20px"
                        height="auto"
                        disabled={uploadingIdx === idx}
                        onClick={() => onClearImage(idx)}
                      >
                        Clear image
                      </Button>
                    </Div>
                  </Div>

                  <Div
                    width="100%"
                    row={isDesktop}
                    gap={isDesktop ? "12px" : "15px"}
                    alignStart
                    justifyStart
                    wrap={!isDesktop ? "wrap" : "nowrap"}
                  >
                    {LANGS.map((lng) => (
                      <Div
                        key={`${card.id}:${lng}`}
                        width={isDesktop ? undefined : "100%"}
                        padding="12px"
                        borderRadius="8px"
                        style={{
                          backgroundColor: "#f0f0f0",
                          flex: isDesktop ? "1 1 0" : undefined,
                          minWidth: isDesktop ? 0 : undefined,
                          boxSizing: "border-box",
                        }}
                        gap="8px"
                      >
                        <P margin="0 0 4px 0" fontWeight="600" textAlign="left">
                          {lng.toUpperCase()}
                        </P>
                        <Div>
                          <P fontSize="12px" margin="0 0 4px 0" textAlign="left">
                            Slide title
                          </P>
                          <FeedbackFormInput
                            value={(card.title && card.title[lng]) || ""}
                            onChange={(e) =>
                              patchCardNested(idx, "title", lng, e.target.value)
                            }
                            aria-label={`Card ${idx + 1} title ${lng}`}
                            style={{ padding: "3px 5px", border: "1px gray solid" }}
                          />
                        </Div>
                        <Div>
                          <P fontSize="12px" margin="0 0 4px 0" textAlign="left">
                            Body HTML
                          </P>
                          <FeedbackFormTextarea
                            as="textarea"
                            rows={12}
                            value={(card.bodyHtml && card.bodyHtml[lng]) || ""}
                            onChange={(e) =>
                              patchCardNested(idx, "bodyHtml", lng, e.target.value)
                            }
                            aria-label={`Card ${idx + 1} body ${lng}`}
                            style={{
                              width: "100%",
                              boxSizing: "border-box",
                            }}
                          />
                        </Div>
                      </Div>
                    ))}
                  </Div>
                </>
              ) : null}
            </Div>
          </Div>
        );
      })}

      <Button
        type="button"
        width="auto"
        outlineButtonGreen
        fontSize="12px"
        padding="10px 20px"
        height="auto"
        onClick={addCard}
      >
        Add card
      </Button>

      {cards.length === 0 && (
        <P textAlign="left" margin="0" fontSize="14px" style={{ color: "#666" }}>
          No content cards yet. Add at least one slide.
        </P>
      )}
    </Div>
  );
}
